import React from 'react';
import {
  ADMIN_CORE_QR_SCENARIOS,
  ADMIN_EXTRA_QR_SCENARIOS,
} from './testQrScenarios';
import './ScannerSystemPresentationPage.css';

const TICKETING_FLOW = [
  {
    title: '1) Issuance + Ownership Lock',
    detail: 'Each ticket/seat is tied to a specific owner and status, with assignment metadata for exact traceability.',
  },
  {
    title: '2) QR Identity + Validation',
    detail: 'Scanner resolves ticket code securely, verifies active status, and checks ownership-linked ticket records.',
  },
  {
    title: '3) Day/Type Rules Applied',
    detail: 'Rules differ by ticket class: PAC AMP event-date gates, festival pass logic, vendor and parking access rules.',
  },
  {
    title: '4) Atomic Commit + Audit Log',
    detail: 'Successful scans are committed once and logged with actor + timestamp for forensic and operational visibility.',
  },
];

const SAFEGUARDS = [
  {
    title: 'Wrong-Day Protection',
    detail: 'PAC AMP and date-bound tickets are blocked on invalid event day attempts.',
  },
  {
    title: 'Duplicate Prevention',
    detail: 'Already-used and same-pass-group same-day duplicate scans are denied with clear operator messaging.',
  },
  {
    title: 'Status Enforcement',
    detail: 'Voided, refunded, cancelled, or invalid states are blocked before any entry can be granted.',
  },
  {
    title: 'Role + Origin Access Control',
    detail: 'Scanner endpoints are restricted to authorized staff/admin users or approved scanner-origin requests.',
  },
  {
    title: 'Stateful Test Harness',
    detail: 'Training/test QR cases include deterministic and stateful scenarios for repeatable staff onboarding.',
  },
  {
    title: 'Operational Recovery',
    detail: 'Controlled reset flows and robust logs support safe troubleshooting without bypassing validation logic.',
  },
];

const SCANNER_DECISION_FLOW = [
  'Read QR payload / code',
  'Resolve ticket + current status',
  'Apply ticket-type gating rules',
  'Apply day/date eligibility checks',
  'Apply duplicate / prior-scan checks',
  'Return allow or deny with explicit reason',
  'Commit successful scan atomically',
  'Write immutable activity + scan metadata',
];

const READINESS_METRICS = [
  { label: 'Validation Model', value: 'Deterministic Rule Engine' },
  { label: 'Scan Outcomes', value: 'Allow / Used / Blocked' },
  { label: 'Decision Scope', value: 'Ticket + Day + Status + Group' },
  { label: 'Auditability', value: 'Per-Scan Actor + Timestamp' },
];

const ALL_SCENARIOS = [
  ...ADMIN_CORE_QR_SCENARIOS.map((scenario) => ({ ...scenario, lane: 'Core' })),
  ...ADMIN_EXTRA_QR_SCENARIOS.map((scenario) => ({ ...scenario, lane: 'Edge Case' })),
];

const toneClassForScenario = (scenario) => {
  const type = String(scenario?.expected?.type || '').toLowerCase();
  const status = String(scenario?.expected?.status || '').toUpperCase();
  if (type === 'success') return 'scanner-system-scenario--ok';
  if (status === 'ALREADY_USED') return 'scanner-system-scenario--used';
  return 'scanner-system-scenario--blocked';
};

const ScannerSystemPresentationPage = () => {
  return (
    <div className="scanner-system-page">
      <section className="scanner-system-hero-wrap">
        <div className="scanner-system-brandbar">
          <div className="scanner-system-brand">
            <img src="/logo.webp" alt="OC MENA Festival" className="scanner-system-brand__logo" />
            <div>
              <div className="scanner-system-brand__title">OC MENA FESTIVAL</div>
              <div className="scanner-system-brand__sub">Scanner Integrity Presentation</div>
            </div>
          </div>
        </div>

        <div className="scanner-system-hero">
          <div className="scanner-system-hero__main">
            <div className="scanner-system-hero__eyebrow">Ticketing + Entry Intelligence</div>
            <h1>How OC MENA Ticketing & Gate Scanning Stay Precise Under Real-World Pressure</h1>
            <p>
              This page presents our ticketing controls, scanner safeguards, and edge-case handling model used to protect
              revenue, prevent duplicate entry, and keep gate decisions fast, explainable, and auditable.
            </p>

            <div className="scanner-system-cta-row">
              <a href="https://blue.ocmenafestival.com" target="_blank" rel="noopener noreferrer" className="scanner-system-btn scanner-system-btn--primary">
                Open Blue Gate Scanner
              </a>
              <a href="https://green.ocmenafestival.com" target="_blank" rel="noopener noreferrer" className="scanner-system-btn scanner-system-btn--primary">
                Open Green Gate Scanner
              </a>
              <a href="https://pacamp.ocmenafestival.com" target="_blank" rel="noopener noreferrer" className="scanner-system-btn scanner-system-btn--primary">
                Open PAC AMP Gate Scanner
              </a>
              <a href="/qr-test" target="_blank" rel="noopener noreferrer" className="scanner-system-btn scanner-system-btn--ghost">
                Open Public QR Test
              </a>
              <a href="/admin?tab=test-qr-codes" target="_blank" rel="noopener noreferrer" className="scanner-system-btn scanner-system-btn--ghost">
                Open Admin QR Lab
              </a>
            </div>
          </div>

          <aside className="scanner-system-hero__aside">
            <h3>Operational Readiness Snapshot</h3>
            <div className="scanner-system-metrics">
              {READINESS_METRICS.map((metric) => (
                <div key={metric.label} className="scanner-system-metric">
                  <span>{metric.label}</span>
                  <strong>{metric.value}</strong>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </section>

      <section className="scanner-system-block">
        <h2>Ticketing Control Flow</h2>
        <div className="scanner-system-grid scanner-system-grid--flow">
          {TICKETING_FLOW.map((step) => (
            <article key={step.title} className="scanner-system-card scanner-system-card--flow">
              <h3>{step.title}</h3>
              <p>{step.detail}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="scanner-system-block">
        <h2>Scanner Decision Engine</h2>
        <div className="scanner-system-decision-list">
          {SCANNER_DECISION_FLOW.map((step, index) => (
            <div key={step} className="scanner-system-decision-item">
              <span>{index + 1}</span>
              <p>{step}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="scanner-system-block">
        <h2>Safeguards & Risk Controls</h2>
        <div className="scanner-system-grid scanner-system-grid--safeguards">
          {SAFEGUARDS.map((item) => (
            <article key={item.title} className="scanner-system-card scanner-system-card--safeguard">
              <h3>{item.title}</h3>
              <p>{item.detail}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="scanner-system-block">
        <h2>Case Scenario Matrix</h2>
        <p className="scanner-system-subtitle">
          These are the training scenarios used to demonstrate expected scanner outcomes for core operations and edge cases.
        </p>
        <div className="scanner-system-grid scanner-system-grid--scenarios">
          {ALL_SCENARIOS.map((scenario) => (
            <article key={scenario.code} className={`scanner-system-card scanner-system-scenario ${toneClassForScenario(scenario)}`}>
              <div className="scanner-system-scenario__top">
                <span className="scanner-system-pill">{scenario.lane}</span>
                <span className="scanner-system-pill scanner-system-pill--muted">{scenario.expected?.status || 'N/A'}</span>
              </div>
              <h3>{scenario.label}</h3>
              <div className="scanner-system-code">{scenario.code}</div>
              <p>{scenario.expected?.message}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ScannerSystemPresentationPage;
